import { useState } from 'react';
import Dialog from './Dialog';
import { en } from '../../locales/en';
import { AsyncAction } from '../../types/general/asyncAction';
import { useGcsStore } from '../../store/useGcsStore';
import type { VehicleId } from '../../types/gcs/vehicle';
import VehicleMissionLabel from '../general/VehicleMissionLabel';
import TextField from '../general/TextField';
import { useLocalizedErrorMessage } from '../../hooks/useLocalizedErrorMessage';

interface SubmitDiscrepancyReportDialogProps {
  gcsId: string;
  vehicleId: VehicleId;
  onClose: () => void;
}

function SubmitDiscrepancyReportDialog({ gcsId, vehicleId, onClose }: SubmitDiscrepancyReportDialogProps) {
  const getLocalizedErrorMessage = useLocalizedErrorMessage();
  const [reportText, setReportText] = useState('');
  const [submitAction, setSubmitAction] = useState<AsyncAction>(AsyncAction.initial());
  const submitDiscrepancyReport = useGcsStore((state) => state.submitDiscrepancyReport);

  const MAX_CHARS = 1000;

  const onSubmitClicked = async () => {
    if (!reportText.trim()) {
      setSubmitAction(AsyncAction.failure(new Error('Report cannot be empty')));
      return;
    }

    try {
      setSubmitAction(AsyncAction.loading());
      await submitDiscrepancyReport(gcsId, vehicleId, reportText);
      setSubmitAction(AsyncAction.success());
      onClose();
    } catch (error) {
      setSubmitAction(AsyncAction.failure(error));
    }
  };

  return (
    <Dialog
      title={en.submitDiscrepancyReportDialog.title}
      onClose={onClose}
      primaryButtonProps={{
        text: en.submitDiscrepancyReportDialog.submitDiscrepancyReportButton,
        callback: onSubmitClicked,
        isLoading: submitAction.isLoading,
        isEnabled: reportText.trim().length > 0 && !submitAction.isLoading
      }}
      errorText={submitAction.isFailure ? getLocalizedErrorMessage(submitAction.error) : undefined}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: 'var(--dialog-common-width)'
        }}
      >
        <VehicleMissionLabel vehicleId={vehicleId} />
        <TextField
          value={reportText}
          onChange={setReportText}
          placeholder={en.submitDiscrepancyReportDialog.reportPlaceholder}
          disabled={submitAction.isLoading}
          maxLength={MAX_CHARS}
          minHeight='200px'
        />
      </div>
    </Dialog>
  );
}

export default SubmitDiscrepancyReportDialog;
